/**
 * Roles API Client
 *
 * API functions for user role management operations
 */

import type { UserRole } from '@hummii/shared-types';
import { buildApiUrl, handleApiResponse, ApiError } from '@shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

/**
 * Response types for role operations
 */
export interface AddRoleResponse {
  success: boolean;
  message?: string;
  user?: {
    id: string;
    roles: UserRole[];
  };
}

export interface SwitchRoleResponse {
  success: boolean;
  message?: string;
}

/**
 * Add a new role to the current user
 * Backend endpoint: POST /users/me/switch-role
 *
 * Note: Backend actually adds the role, not just switches.
 * The method will throw 400 error if user already has the role.
 *
 * @param role - The role to add (CLIENT or CONTRACTOR)
 * @returns Promise<AddRoleResponse>
 */
export async function addRoleToUser(role: UserRole): Promise<AddRoleResponse> {
  const url = buildApiUrl('/users/me/switch-role');
  console.log('[roles.api] addRoleToUser - URL:', url);
  console.log('[roles.api] addRoleToUser - Role:', role);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ role }),
    });

    console.log('[roles.api] addRoleToUser - Response status:', response.status);
    console.log('[roles.api] addRoleToUser - Response ok:', response.ok);

    const result = await handleApiResponse<AddRoleResponse>(response);
    console.log('[roles.api] addRoleToUser - Success:', result);
    return result;
  } catch (error) {
    console.error('[roles.api] addRoleToUser - Error:', error);
    throw error;
  }
}

/**
 * Switch active role for current session
 * Backend endpoint: POST /users/me/switch-role
 *
 * @param role - The role to switch to (CLIENT or CONTRACTOR)
 * @returns Promise<SwitchRoleResponse>
 */
export async function switchUserRole(role: UserRole): Promise<SwitchRoleResponse> {
  const url = buildApiUrl('/users/me/switch-role');
  console.log('[roles.api] switchUserRole - URL:', url);
  console.log('[roles.api] switchUserRole - Role:', role);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ role }),
    });

    console.log('[roles.api] switchUserRole - Response status:', response.status);
    console.log('[roles.api] switchUserRole - Response ok:', response.ok);

    const result = await handleApiResponse<SwitchRoleResponse>(response);
    console.log('[roles.api] switchUserRole - Success:', result);
    return result;
  } catch (error) {
    console.error('[roles.api] switchUserRole - Error:', error);
    throw error;
  }
}

/**
 * Get current user profile (includes roles)
 * Backend endpoint: GET /users/me
 *
 * @returns Promise<User>
 */
export async function getCurrentUserProfile() {
  const url = buildApiUrl('/users/me');
  console.log('[roles.api] getCurrentUserProfile - URL:', url);

  try {
    const response = await fetchWithTokenRefresh(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
    });

    console.log('[roles.api] getCurrentUserProfile - Response status:', response.status);
    console.log('[roles.api] getCurrentUserProfile - Response ok:', response.ok);

    const result = await handleApiResponse<any>(response);
    console.log('[roles.api] getCurrentUserProfile - Success:', result);
    return result;
  } catch (error) {
    console.error('[roles.api] getCurrentUserProfile - Error:', error);
    throw error;
  }
}
